import type { SlimeState } from '../slime/slime-state.js';

export interface CombatSkill {
  id: string;
  name: string;
  description: string;
  energyCost: number;
  baseDamage: number;
  element: 'water' | 'acid' | 'physical';
  cooldownSec: number;
  rangeMeters: number;
}

export const COMBAT_SKILLS: Record<string, CombatSkill> = {
  water_shot: {
    id: 'water_shot',
    name: 'Water Shot',
    description: 'Fires a high-pressure jet of compressed water.',
    energyCost: 15,
    baseDamage: 25,
    element: 'water',
    cooldownSec: 1.5,
    rangeMeters: 14.0
  },
  acid_spit: {
    id: 'acid_spit',
    name: 'Acid Spit',
    description: 'Corrosive projectile dissolving enemy armor.',
    energyCost: 25,
    baseDamage: 40,
    element: 'acid',
    cooldownSec: 3.0,
    rangeMeters: 10.0
  },
  body_slam: {
    id: 'body_slam',
    name: 'Heavy Body Slam',
    description: 'Compresses slime mass to slam downwards creating a shockwave.',
    energyCost: 30,
    baseDamage: 55,
    element: 'physical',
    cooldownSec: 4.0,
    rangeMeters: 4.0
  }
};

export function calculateDamage(attackerDamage: number, targetDefense: number = 0): number {
  const reduction = 100 / (100 + Math.max(0, targetDefense));
  return Math.max(1, Math.round(attackerDamage * reduction));
}

export class CombatSkillManager {
  private cooldowns = new Map<string, number>();

  public canCast(skillId: string, currentEnergy: number): { allowed: boolean; reason?: string } {
    const skill = COMBAT_SKILLS[skillId];
    if (!skill) {
      return { allowed: false, reason: `Unknown skill: ${skillId}` };
    }

    const currentCd = this.cooldowns.get(skillId) ?? 0;
    if (currentCd > 0) {
      return { allowed: false, reason: `Skill on cooldown (${currentCd.toFixed(1)}s)` };
    }

    if (currentEnergy < skill.energyCost) {
      return { allowed: false, reason: `Insufficient gel energy: requires ${skill.energyCost}` };
    }

    return { allowed: true };
  }

  public cast(skillId: string, slimeState: SlimeState): { success: boolean; skill?: CombatSkill } {
    const check = this.canCast(skillId, slimeState.gelEnergy);
    if (!check.allowed) {
      return { success: false };
    }

    const skill = COMBAT_SKILLS[skillId];
    slimeState.consumeEnergy(skill.energyCost);
    this.cooldowns.set(skillId, skill.cooldownSec);

    return { success: true, skill };
  }

  public getCooldownRemaining(skillId: string): number {
    return this.cooldowns.get(skillId) ?? 0;
  }

  public update(dt: number): void {
    for (const [id, cd] of this.cooldowns.entries()) {
      const next = Math.max(0, cd - dt);
      if (next === 0) {
        this.cooldowns.delete(id);
      } else {
        this.cooldowns.set(id, next);
      }
    }
  }
}
